import { create } from 'zustand';
import { useConnection } from './connection';
import { useShellNav } from './shellNav';

/**
 * Telling somebody, plainly, that what they just saved is kept on this PC and
 * not with the server yet (.docs/023).
 *
 * A note at the foot of a form was too easy to miss: offline, people pressed
 * Save again, or wondered whether anything had happened at all. So work kept
 * for later is said in a dialog, which also says what happens next — nothing,
 * on their part.
 */

/** What the person chose: carry on typing, go back to the list, or see Offline work. */
export type SavedChoice = 'another' | 'close' | 'waiting';

export interface SavedOfflineRequest {
  /** What was saved, as the outbox describes it: "Stock IN 03 Nov: 4 line(s)". */
  what: string;
  /** The line is up but the server has not answered in time, rather than no line. */
  slowLine?: boolean;
  /** A create form that can take the next one at once. */
  canEnterAnother?: boolean;
  /** The button that just closes it. "Back to list" on a form, "OK" elsewhere. */
  closeLabel?: string;
  /** Anything more worth saying: "Its code is given when it is sent." */
  note?: string;
}

interface SavedOfflineState {
  open: (SavedOfflineRequest & { resolve: (choice: SavedChoice) => void }) | null;
}

export const useSavedOffline = create<SavedOfflineState>(() => ({ open: null }));

/**
 * Shows the dialog and resolves with the choice. "See offline work" goes to
 * Offline work once the caller has done with its form.
 */
export function announceSavedOffline(request: SavedOfflineRequest): Promise<SavedChoice> {
  return new Promise((resolve) => {
    // Another save while one is showing: the first simply closes.
    useSavedOffline.getState().open?.resolve('close');
    useSavedOffline.setState({
      open: {
        // With the line up, no answer means a slow server, not no connection.
        slowLine: useConnection.getState().online,
        ...request,
        resolve: (choice) => {
          useSavedOffline.setState({ open: null });
          resolve(choice);
          // After the form has closed or cleared, so leaving it asks nothing.
          if (choice === 'waiting') setTimeout(() => useShellNav.getState().navigate('home-stuck'), 0);
        },
      },
    });
  });
}
